import {effect, inject, Injectable, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, Subscription} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {ROLE} from '../utils/enums/role';
import {environment} from '../../config/environments/environment';
import {User} from '../models/user';
import {UserService} from './user.service';
import {MarketService} from './market.service';
import {KeycloakEventType, ReadyArgs, typeEventArgs} from 'keycloak-angular';
import {KeycloakWrapperService} from '../utils/keycloak/keycloak-wrapper.service';
import {NewsletterService} from './newsletter.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    public isLogged$: BehaviorSubject<boolean>;
    public isRefreshing: boolean;

    private _isSubscribing = false;
    private _loggedAppUser: User;
    private readonly _router = inject(Router);
    private readonly _keycloakService = inject(KeycloakWrapperService);
    private readonly _userService = inject(UserService);
    private readonly _marketService = inject(MarketService);
    private readonly _newsletterService = inject(NewsletterService);

    constructor() {
        this.watchKeycloakEvents();
    }

    private watchKeycloakEvents(): void {
        effect(() => {
            const kcSignal = this._keycloakService.keycloakSignal;
            let assignFn: (arg: boolean) => void;
            if (this.isLogged$) {
                assignFn = (arg) => this.isLogged$.next(arg);
            } else {
                assignFn = (arg) => this.initAndWatchLogged(arg);
            }
            switch (kcSignal?.type) {
                case KeycloakEventType.KeycloakAngularInit:
                case KeycloakEventType.Ready:
                    assignFn(typeEventArgs<ReadyArgs>(kcSignal.args) ?? this._keycloakService.authenticated);
                    if (this.isCurrentlyLogged()) {
                        this._keycloakService.getKeycloakInstance().loadUserProfile().finally(
                            () => this.loadAppUserProfile().add(
                                () => this._newsletterService.loadNewsletterReadStatus()
                            )
                        );
                    }
                    break;
                case KeycloakEventType.KeycloakAngularNotInitialized:
                case KeycloakEventType.AuthError:
                case KeycloakEventType.AuthRefreshError:
                case KeycloakEventType.AuthLogout:
                    assignFn(false);
                    break;
            }
        });
    }


    public ngOnDestroy(): void {
        this.isLogged$.complete();
    }

    private initAndWatchLogged(initVal: boolean): void {
        this.isLogged$ = new BehaviorSubject<boolean>(initVal);
        this.subscribeToLoggedSubject();
    }

    private subscribeToLoggedSubject(): void {
        this.isLogged$.subscribe((val: boolean) => {
            if (this._isSubscribing) {
                this._isSubscribing = false;
            } else if (val) {
                this._router.navigate(['introduction-map']);
            }
        });
    }

    private loadAppUserProfile(): Subscription {
        const subscription = this._userService.getUserByEmail(this.userEmail).subscribe({
            next: (response) => this.handleFetchLoggedUser(response),
            error: () => this.handleFetchLoggedUserError()
        });
        return subscription;
    }

    private handleFetchLoggedUser(user: User): void {
        this._loggedAppUser = user;
        this._marketService.setMarketById(user.marketId, true);
    }

    private handleFetchLoggedUserError(): void {
        this.logout();
    }

    public isCurrentlyLogged(): boolean {
        return this.isLogged$?.getValue() ?? false;
    }

    public logout(): void {
        this._keycloakService.logout().then(() => this.isLogged$.next(false));
    }

    public set loggedUserContactName(contactName: string) {
        if (this._loggedAppUser) {
            this._loggedAppUser.contactName = contactName;
        } else {
            this.loadAppUserProfile().add(() => this._loggedAppUser.contactName = contactName);
        }
    }

    public get loggedUserContactName(): string {
        if (!this._loggedAppUser) this.loadAppUserProfile();
        return this._loggedAppUser?.contactName;
    }

    public get keycloakLanguage(): string {
        const token = this._keycloakService.getKeycloakInstance()?.token;
        return token ? jwtDecode(token)['locale'] : null;
    }

    public get userName(): string {
        return this._keycloakService.getKeycloakInstance()?.profile?.username;
    }

    public get userEmail(): string {
        return this._keycloakService.getKeycloakInstance().profile?.email;
    }

    public get userRole(): ROLE {
        return <ROLE>this._keycloakService.getKeycloakInstance().tokenParsed?.resource_access[environment.keycloak.clientId].roles
            .find(role => role === ROLE.ADMIN) ?? ROLE.USER;
    }

    public get loggedUser(): User {
        return this._loggedAppUser;
    }

}
